import { QueryObserverResult, useQuery } from '@tanstack/react-query';
import type { TaskRecord } from '../../containers/tasks-table/tasks-table.type';
import {
  getAdherenceLogs,
  getAgentEventLogs,
  getAgentEventRelatedTask,
  getAgentEventRelatedTasks,
  getAgentFinancialData,
  getAgentSessions,
} from './events-client';
import type {
  AdherenceLog,
  AdherenceLogEventType,
  AgentEventLog,
  AgentEventTypes,
  AgentFinancialResponse,
  AgentSession,
  AgentSource,
} from './events-client.type';

/**
 * Hook to fetch adherence logs for a specific organization and event type
 *
 * @param {string} organizationId - The ID of the organization to fetch adherence logs for
 * @param {AdherenceLogEventType[]} eventTypes - The types of events to fetch adherence logs for
 * @returns {Object} Object containing:
 *   - data: The adherence logs data
 *   - query: The React Query result object
 */
const useAdherenceLogs = (
  organizationId: string | undefined,
  eventTypes?: AdherenceLogEventType[],
): {
  data: AdherenceLog[] | undefined;
  query: QueryObserverResult<AdherenceLog[]>;
} => {
  const query = useQuery({
    queryKey: ['adherence-logs', organizationId, eventTypes],
    queryFn: () => getAdherenceLogs(organizationId || '', eventTypes),
    enabled: !!organizationId && !!eventTypes && eventTypes.length > 0,
  });

  return { data: query.data, query };
};

/**
 * Hook to fetch agent event logs with optional filtering
 *
 * @param {Object} params - The filtering parameters
 * @param {string} [params.sessionId] - Filter by session ID
 * @param {AgentEventTypes} [params.eventType] - Filter by event type
 * @param {AgentSource} [params.agentSource] - Filter by agent source
 * @param {string} [params.userId] - Filter by user ID
 * @param {string} [params.taskId] - Filter by task ID
 * @param {boolean} [enabled] - Whether to enable the query (defaults to true if required params are provided)
 * @returns {Object} Object containing:
 *   - data: The agent event logs data
 *   - query: The React Query result object
 */
const useAgentEventLogs = (
  params: {
    sessionId?: string;
    eventType?: AgentEventTypes;
    agentSource?: AgentSource;
    userId?: string;
    taskId?: string;
  },
  enabled?: boolean,
): {
  data: AgentEventLog[] | undefined;
  query: QueryObserverResult<AgentEventLog[]>;
} => {
  const hasRequiredParams = !!(params.taskId || params.sessionId);
  const isEnabled = enabled !== undefined ? enabled : hasRequiredParams;

  const query = useQuery({
    queryKey: [
      'agent-event-logs',
      params.sessionId ?? null,
      params.eventType ?? null,
      params.agentSource ?? null,
      params.userId ?? null,
      params.taskId ?? null,
    ],
    queryFn: () => getAgentEventLogs(params),
    enabled: isEnabled,
  });

  return { data: query.data, query };
};

/**
 * Hook to fetch agent event related tasks with filtering by portfolio, project, or subproject
 *
 * @param {Object} params - The filtering parameters
 * @param {string} [params.portfolioId] - Filter by portfolio ID
 * @param {string} [params.projectId] - Filter by project ID
 * @param {string} [params.subprojectId] - Filter by subproject ID
 * @param {string} params.startDate - Start date for the query
 * @param {string} params.endDate - End date for the query
 * @param {boolean} [enabled] - Whether to enable the query (defaults to true if required params are provided)
 * @returns {Object} Object containing:
 *   - data: The agent event related tasks data
 *   - query: The React Query result object
 */
const useAgentEventRelatedTasks = (
  params: {
    portfolioId?: string;
    projectId?: string;
    subprojectId?: string;
    startDate: string;
    endDate: string;
  },
  enabled?: boolean,
): {
  data: TaskRecord[] | undefined;
  query: QueryObserverResult<TaskRecord[]>;
} => {
  const hasRequiredScope = !!(params.portfolioId || params.projectId || params.subprojectId);
  const hasRequiredDates = !!(params.startDate && params.endDate);
  const isValidRange = hasRequiredDates && new Date(params.startDate) <= new Date(params.endDate);
  const isEnabled = enabled !== undefined ? enabled : hasRequiredScope && hasRequiredDates && isValidRange;

  const query = useQuery({
    queryKey: [
      'agent-event-related-tasks',
      params.portfolioId ?? null,
      params.projectId ?? null,
      params.subprojectId ?? null,
      params.startDate,
      params.endDate,
    ],
    queryFn: () => getAgentEventRelatedTasks(params),
    enabled: isEnabled,
  });

  return { data: query.data, query };
};

/**
 * Hook to fetch an agent event related task by ID
 *
 * @param {string} taskId - The ID of the task to fetch
 * @returns {Object} Object containing:
 *   - data: The agent event related task data
 *   - query: The React Query result object
 */
const useAgentEventRelatedTask = (
  taskId: string,
): {
  data: TaskRecord | undefined;
  query: QueryObserverResult<TaskRecord>;
} => {
  const query = useQuery({
    queryKey: ['agent-event-related-task', taskId],
    queryFn: () => getAgentEventRelatedTask(taskId),
    enabled: !!taskId && taskId !== '',
  });

  return { data: query.data, query };
};

/**
 * Hook to fetch agent sessions for a given organization and date range
 *
 * @param {Object} params - The filtering parameters
 * @param {string} params.organizationId - The ID of the organization to fetch agent sessions for
 * @param {string} params.startDate - The start date of the date range
 * @param {string} params.endDate - The end date of the date range
 * @returns {Object} Object containing:
 *   - data: The agent sessions data
 *   - query: The React Query result object
 */
const useAgentSessions = (params: {
  organizationId: string;
  startDate: string;
  endDate: string;
}): {
  data: AgentSession[] | undefined;
  query: QueryObserverResult<AgentSession[]>;
} => {
  const query = useQuery({
    queryKey: ['agent-sessions', params.organizationId, params.startDate, params.endDate],
    queryFn: () => getAgentSessions(params),
    enabled: !!params.organizationId && !!params.startDate && !!params.endDate,
  });

  return { data: query.data, query };
};

/**
 * Hook to fetch agent financial data for a given organization and date range
 *
 * @param {Object} params - The filtering parameters
 * @param {string} params.organizationId - The ID of the organization to fetch agent financial data for
 * @param {string} params.startDate - The start date of the date range
 * @param {string} params.endDate - The end date of the date range
 * @returns {Object} Object containing:
 *   - data: The agent financial data
 *   - query: The React Query result object
 */
const useAgentFinancialData = (params: {
  organizationId: string;
  startDate: string;
  endDate: string;
}): {
  data: AgentFinancialResponse | undefined;
  query: QueryObserverResult<AgentFinancialResponse>;
} => {
  const query = useQuery({
    queryKey: ['agent-financial-data', params.organizationId, params.startDate, params.endDate],
    queryFn: () => getAgentFinancialData(params),
    enabled: !!params.organizationId && !!params.startDate && !!params.endDate,
  });

  return { data: query.data, query };
};

export {
  useAdherenceLogs,
  useAgentEventLogs,
  useAgentEventRelatedTask,
  useAgentEventRelatedTasks,
  useAgentFinancialData,
  useAgentSessions,
};
